/**
 * useProfile Hook
 * 
 * Fetches and caches user profile data from the API
 * Should be called after successful login to get profile details
 */

import { useEffect, useState } from 'react';
import { getProfile } from '../services/profileService';
import { setUserProfile, getUserProfile } from '../utils/tokenManager';

export const useProfile = () => {
  const [profile, setProfile] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as true for initial load
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    // Check if we already have profile in localStorage
    const cachedProfile = getUserProfile();
    if (cachedProfile) {
      setProfile(cachedProfile);
      setIsLoading(false); // Set loading false immediately if cached
      return;
    }

    // Fetch from API
    setIsLoading(true);
    setError(null);

    try {
      const response = await getProfile();
      
      if (response.success && response.data?.profile) {
        const profileData = response.data.profile;
        setProfile(profileData);
        setUserProfile(profileData);
        console.log('✅ Profile fetched and cached:', {
          business_name: profileData.business_name,
          name: `${profileData.first_name} ${profileData.second_name}`
        });
      } else {
        throw new Error(response.message || 'Failed to fetch profile');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch profile';
      console.error('❌ Profile fetch error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return {
    profile,
    isLoading,
    error,
    refetch: fetchProfile
  };
};

